import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Link,
  Outlet,
  redirect,
} from "@tanstack/react-router";
import { z } from "zod";
import { RootLayout } from "./routes/__root.js";
import { Splash } from "./auth/clerk-lazy.js";

function RoutePending() {
  return (
    <div role="status" aria-label="Loading page" data-testid="route-pending">
      {Splash}
    </div>
  );
}

function RouteError() {
  return (
    <div role="alert" className="min-h-[60vh] flex items-center justify-center px-6">
      <div className="max-w-sm text-center space-y-4">
        <h1 className="text-xl font-light">Couldn't open this page</h1>
        <p className="text-sm text-white/55">
          Check your connection and reload to try again.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            className="mx-btn-primary"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
          <Link to="/docs" className="mx-btn-ghost">Back to docs</Link>
        </div>
      </div>
    </div>
  );
}

const rootRoute = createRootRoute({
  component: () => (
    <RootLayout>
      <Outlet />
    </RootLayout>
  ),
});

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: lazyRouteComponent(() => import("./routes/landing.js"), "LandingPage"),
});

const docsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs",
  component: lazyRouteComponent(() => import("./routes/docs.js"), "DocsPage"),
});

const docSessionRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/$docId",
  parseParams: (params) => ({ docId: z.string().uuid().parse(params.docId) }),
  stringifyParams: (params) => ({ docId: params.docId }),
  remountDeps: ({ params }) => [params.docId],
  // Optional starter prompt (b2c onboarding): the docs empty state can
  // create a deck and hand its composer a first brief to send.
  validateSearch: (search: Record<string, unknown>): { prompt?: string } =>
    typeof search.prompt === "string" ? { prompt: search.prompt } : {},
  component: lazyRouteComponent(() => import("./routes/doc.js"), "DocSessionPage"),
});

// The presenter view: a second window of the deck, driven from the show
// (docs/plans/presenter-notes.md §6). A sibling of the editor route, not a
// child, so the editor's remount/search handling stays its own.
const presenterRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/docs/$docId/presenter",
  parseParams: (params) => ({ docId: z.string().uuid().parse(params.docId) }),
  stringifyParams: (params) => ({ docId: params.docId }),
  component: lazyRouteComponent(() => import("./routes/presenter.js"), "PresenterPage"),
});

const onboardingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/onboarding",
  component: lazyRouteComponent(() => import("./routes/onboarding.js"), "OnboardingPage"),
});

// /brand is the Brand Studio HOME (the mockup's Overview) — the studio has a
// single top-level entry point (docs/plans/brand-studio-ui-mockup.md §3.1).
const brandRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand",
  component: lazyRouteComponent(() => import("./routes/brand-overview.js"), "BrandOverviewPage"),
});


const brandTemplatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand/templates",
  component: lazyRouteComponent(() => import("./routes/brand-studio.js"), "BrandTemplatesPage"),
});

const brandCanvasRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand/templates/$groupId",
  // Optional canvas doc id, so a fresh load can reuse an already-materialised
  // canvas rather than re-ensuring; the page ensures one when absent.
  validateSearch: (search: Record<string, unknown>): { doc?: string } =>
    typeof search.doc === "string" ? { doc: search.doc } : {},
  // A same-route param change (one group's canvas -> another's) must REMOUNT
  // the page: BrandCanvasPage seeds its doc id from the mount-time search
  // param, and without a remount a LATE navigation — a slow "Open group"
  // POST resolving after the user moved to a different group — flipped the
  // URL while the view kept streaming the old group. The next refresh then
  // truthfully rendered the OTHER group ("talk to a template slide, refresh,
  // it shows one of the other groups").
  remountDeps: ({ params }) => [params.groupId],
  component: lazyRouteComponent(() => import("./routes/brand-canvas.js"), "BrandCanvasPage"),
});

const brandHistoryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand/history",
  component: lazyRouteComponent(() => import("./routes/brand-studio.js"), "BrandHistoryPage"),
});

const brandIdentityRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand/identity",
  // ?edit=1 opens the Edit-values modal straight away (the Overview links here
  // for precise editing now the standalone settings page is retired).
  validateSearch: (search: Record<string, unknown>): { edit?: number } =>
    search.edit ? { edit: 1 } : {},
  component: lazyRouteComponent(() => import("./routes/brand-identity.js"), "BrandIdentityPage"),
});

const brandMaterialsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand/materials",
  component: lazyRouteComponent(() => import("./routes/brand-studio.js"), "BrandMaterialsPage"),
});

// The asset library lives on Materials now (one upload control for files,
// images and fonts); the old address keeps working for bookmarks and specs.
const brandLibraryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/brand/library",
  beforeLoad: () => {
    throw redirect({ to: "/brand/materials" });
  },
  // Never rendered (beforeLoad redirects), but every page route carries a
  // preloadable component (router.test.ts) and this is the page it stands for.
  component: lazyRouteComponent(() => import("./routes/brand-studio.js"), "BrandMaterialsPage"),
});

const teamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/team",
  component: lazyRouteComponent(() => import("./routes/team.js"), "TeamPage"),
});

// MCP OAuth consent. The OAuth server (oidc-provider, on the API) redirects
// here when a client needs approval; the path is ITS choice, because it scopes
// the interaction cookie to this pathname and the page's own API calls sit
// beneath it. Deliberately NOT a public route: approving access requires a
// signed-in user, and the auth gate's sign-in flow preserves the URL so the
// pending request resumes afterwards. The uid identifies nothing on its own —
// the server pairs it with the cookie.
const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/mcp/oauth/interaction/$uid",
  component: lazyRouteComponent(
    () => import("./routes/oauth-consent.js"),
    "OAuthConsentPage",
  ),
});

// Team-invitation accept page — publicly reachable (the token is the
// credential); ClerkGate lists it as a public route.
const inviteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/invite/$token",
  component: lazyRouteComponent(() => import("./routes/invite.js"), "InvitePage"),
});

const billingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/billing",
  component: lazyRouteComponent(() => import("./routes/billing.js"), "BillingPage"),
});

const pricingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/pricing",
  component: lazyRouteComponent(() => import("./routes/pricing.js"), "PricingPage"),
});

const contactRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/contact",
  component: lazyRouteComponent(() => import("./routes/contact.js"), "ContactPage"),
});

const adminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: lazyRouteComponent(() => import("./routes/admin.js"), "AdminPage"),
});

const privacyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/privacy",
  component: lazyRouteComponent(() => import("./routes/privacy.js"), "PrivacyPage"),
});

const termsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/terms",
  component: lazyRouteComponent(() => import("./routes/terms.js"), "TermsPage"),
});

// DEV-ONLY: a component gallery of the b2c-only surfaces (pricing, change-plan,
// impersonation top-bar, onboarding wait) so they can be screenshotted + e2e'd
// in dev-bypass mode without Clerk/Stripe creds. Tree-shaken from prod builds
// (import.meta.env.DEV is statically false there), so it never ships.
const devRoutes = import.meta.env.DEV
  ? [
      createRoute({
        getParentRoute: () => rootRoute,
        path: "/__ui-preview",
        component: lazyRouteComponent(() => import("./routes/UiPreview.js"), "UiPreview"),
      }),
    ]
  : [];

const routeTree = rootRoute.addChildren([
  indexRoute,
  docsRoute,
  docSessionRoute,
  presenterRoute,
  onboardingRoute,
  brandRoute,
  brandTemplatesRoute,
  brandCanvasRoute,
  brandHistoryRoute,
  brandIdentityRoute,
  brandMaterialsRoute,
  brandLibraryRoute,
  teamRoute,
  oauthConsentRoute,
  inviteRoute,
  billingRoute,
  pricingRoute,
  contactRoute,
  adminRoute,
  privacyRoute,
  termsRoute,
  ...devRoutes,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadDelay: 100,
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 250,
  defaultPendingMinMs: 150,
  defaultErrorComponent: RouteError,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
